import React, { useState, useEffect, useCallback } from 'react';
import LogoCarousel from '../components/LogoCarousel';
import { partnersCustomersAPI } from '../services/api';
import { partners as mockPartners, customers as mockCustomers } from '../data/mock';

const PartnersCustomers = () => {
  const [partners, setPartners] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [partnersRes, customersRes] = await Promise.all([
        partnersCustomersAPI.getAll('partner'),
        partnersCustomersAPI.getAll('customer')
      ]);
      setPartners(partnersRes.data.length > 0 ? partnersRes.data : mockPartners);
      setCustomers(customersRes.data.length > 0 ? customersRes.data : mockCustomers);
    } catch {
      setPartners(mockPartners);
      setCustomers(mockCustomers);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadData();
  }, [loadData]);

  if (isLoading) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <section className="bg-gradient-to-br from-emerald-600 to-emerald-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Partners & Customers</h1>
          <p className="text-xl text-emerald-50 max-w-3xl mx-auto">
            Trusted by industry leaders across India's renewable energy sector
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Partners</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Collaborating with leading organizations to deliver excellence in renewable energy
            </p>
          </div>
          <LogoCarousel logos={partners} direction="left" />
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Customers</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Serving developers, EPC companies, and industrial clients nationwide
            </p>
          </div>
          <LogoCarousel logos={customers} direction="right" />
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-emerald-600 to-emerald-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center text-white">
            <div>
              <div className="text-5xl font-bold mb-2">250+</div>
              <p className="text-xl font-medium">MW Delivered</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">45+</div>
              <p className="text-xl font-medium">PPAs Executed</p>
            </div>
            <div>
              <div className="text-5xl font-bold mb-2">100%</div>
              <p className="text-xl font-medium">Client Satisfaction</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Become Our Partner</h2>
          <p className="text-xl text-gray-600 mb-8">
            Join our network of successful renewable energy stakeholders
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg"
          >
            Get In Touch
          </a>
        </div>
      </section>
    </div>
  );
};

export default PartnersCustomers;
